import { Toast } from '../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { callAPI } from '../../../services/api.service';

// Classroom "Ship stories" flow — moved out of Debug.service, where the production teacher
// shipping calls had been living alongside actual debug helpers.

export interface ShipPreviewOrder {
  orderNumber: number;
  bookId: string;
  bookTitle: string | null;
  authorName: string | null;
  quantity: number;
  willSkip: boolean;
  skipReasons: string[];
}

export interface ShipPreviewPreOrder {
  studentEmail: string;
  studentName: string;
  count: number;
  /** True when the student has no print-ready book to assign — the release will skip them. */
  willSkip: boolean;
  skipReasons: string[];
}

export interface ShipPreview {
  orders: ShipPreviewOrder[];
  preOrders: ShipPreviewPreOrder[];
  totalOrders: number;
  totalQuantity: number;
  skippedCount: number;
  alreadyReleased: boolean;
}

// Server-computed mirror of what /bulk/ship/all will actually ship. The dialog and the
// Ship stories button both use this instead of deriving a count from book counters.
// Returns 'error' on any failure — callers must treat that as "unknown", NEVER as "nothing
// skipped": a swallowed failure here used to silently disable the partial-ship guard.
export const fetchShipPreview = async (
  classroomId: string | null | undefined,
): Promise<ShipPreview | 'error' | null> => {
  if (!classroomId) return null;
  try {
    const response = await callAPI(`api/book-purchase/bulk/ship/preview`, { classroomId }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      return {
        orders: response.orders || [],
        // Tolerate older server payloads that predate the willSkip flag.
        preOrders: (response.preOrders || []).map((p: any) => ({
          willSkip: false,
          skipReasons: [],
          ...p,
        })),
        totalOrders: response.totalOrders || 0,
        totalQuantity: response.totalQuantity || 0,
        skippedCount: response.skippedCount || 0,
        alreadyReleased: !!response.alreadyReleased,
      };
    }
  } catch (error) {}
  return 'error';
};

export const shipAllBooks = async (
  classroomId: string | null | undefined,
  projectId: string | null | undefined,
  studentPreOrderBook: Record<string, string> | null,
  setLoading: Function,
  onSuccess?: () => void,
): Promise<void> => {
  try {
    setLoading(true);
    const response = await callAPI(
      `api/book-purchase/bulk/ship/all`,
      { studentPreOrderBook, classroomId, projectId },
      'POST',
    );

    if (response?.status === HTTPStatusEnum.OK) {
      if (response?.partialFailure) {
        // Some orders could not be shipped (incomplete books) — warn instead of reporting full
        // success, and surface the most relevant reason when available.
        const firstReason = Object.values(
          (response?.skippedReasons as Record<string, string[]>) || {},
        )
          .map((reasons) => reasons?.[0])
          .find(Boolean);
        const base =
          response?.message ||
          'Some books stayed behind — fix them and send a follow-up shipment when ready.';
        Toast(firstReason ? `${base} (${firstReason})` : base, true);
      } else {
        Toast(response?.message || 'Class Shipment sent — your books are being printed');
      }
      onSuccess && onSuccess();
    } else if (response?.nothingToShip) {
      // Not a failure — nothing was pending (already shipped / new copies auto-ship). Reassure.
      Toast(
        response?.message ||
          'Nothing is waiting for the Class Shipment right now. New orders ship automatically to each buyer.',
        false,
      );
    } else if (response?.error) {
      Toast(response?.error, true);
    } else {
      // callAPI resolves null on its client-side timeout and on network errors instead of
      // throwing — without this branch the spinner stopped with no feedback at all.
      Toast(
        "The shipment request didn't get a response — it may still be processing. Refresh in a minute before retrying.",
        true,
      );
    }
  } catch (error) {
    Toast('Error ship all books', true);
  } finally {
    setLoading(false);
  }
};

export const shipBooksConfirmation = async (
  classroomId: string | null | undefined,
  projectId: string | null,
): Promise<void> => {
  try {
    await callAPI(`api/book-purchase/bulk/ship/confirmation`, { classroomId, projectId });
  } catch (error) {}
};
